/**
 * Bedridden Patients Map with District Polygons
 *
 * Uses GeoJSON boundaries to show district areas colored by patient concentration
 * Based on VulnerableMapPolygon structure
 */

import { useEffect, useState } from 'react'
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { AlertTriangle, BedSingle } from 'lucide-react'
import { MapErrorBoundary } from './MapErrorBoundary'
import { logger } from '@/shared/utils/logger'

interface DistrictBedridden {
  district_id: number
  district_name_th: string
  district_name_en: string
  count: number
  concentration_level: 'low' | 'medium' | 'high'
  by_type?: { home: number; bed: number }
  by_severity?: { high: number; medium: number; low: number }
}

interface BedriddenMapPolygonProps {
  districts?: DistrictBedridden[]
  className?: string
  year?: number
  selectedDistrictId?: number | null
  selectedDistrictName?: string | null
}

/**
 * Calculate concentration level based on patient count
 */
const getConcentrationLevel = (count: number): 'low' | 'medium' | 'high' => {
  if (count >= 50) return 'high'
  if (count >= 20) return 'medium'
  return 'low'
}

/**
 * Color by concentration level (Korat theme)
 */
const getConcentrationColor = (level: 'low' | 'medium' | 'high', isHovered: boolean = false) => {
  const opacity = isHovered ? 0.85 : 0.65
  switch (level) {
    case 'high':
      return `rgba(239, 68, 68, ${opacity})` // Red 500 - เข้มข้นสูง
    case 'medium':
      return `rgba(249, 115, 22, ${opacity})` // Orange 500 - เข้มข้นกลาง
    case 'low':
      return `rgba(34, 197, 94, ${opacity})` // Green 500 - เข้มข้นต่ำ
  }
}

const getConcentrationLabel = (level: 'low' | 'medium' | 'high') => {
  switch (level) {
    case 'high': return 'เข้มข้น'
    case 'medium': return 'ปานกลาง'
    case 'low': return 'น้อย'
  }
}

/**
 * Inner component that applies GeoJSON to the map
 */
function MapDataLayer({
  districts,
  geoJsonData
}: {
  districts: DistrictBedridden[]
  geoJsonData: any
}) {
  const map = useMap()

  // Map GeoJSON district names to our data
  const getDistrictData = (districtNameEn: string): DistrictBedridden | undefined => {
    return districts.find(d => d.district_name_en === districtNameEn)
  }

  useEffect(() => {
    if (!map || !geoJsonData) {
      logger.debug('⏳ Waiting for map or GeoJSON...')
      return
    }

    logger.debug('🗺️ Applying bedridden patient polygons...', {
      districtPolygons: geoJsonData.features.length,
      dataDistricts: districts.length
    })

    // Clear existing data layers
    map.data.forEach(feature => map.data.remove(feature))

    // Filter GeoJSON features if specific districts are selected
    const districtNamesEn = districts.map(d => d.district_name_en)
    const filteredGeoJson = districts.length < 32 ? {
      ...geoJsonData,
      features: geoJsonData.features.filter((f: any) =>
        districtNamesEn.includes(f.properties.district_name_en)
      )
    } : geoJsonData

    // Add GeoJSON features
    const addedFeatures = map.data.addGeoJson(filteredGeoJson)
    logger.debug('✅ Added', addedFeatures.length, 'district polygons')

    // Center map on selected district if only one
    if (districts.length === 1 && filteredGeoJson.features.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      filteredGeoJson.features.forEach((feature: any) => {
        if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach((coord: number[]) => {
            bounds.extend({ lat: coord[1], lng: coord[0] })
          })
        } else if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach((polygon: number[][][]) => {
            polygon[0].forEach((coord: number[]) => {
              bounds.extend({ lat: coord[1], lng: coord[0] })
            })
          })
        }
      })
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
    }

    let currentHoveredFeature: google.maps.Data.Feature | null = null

    // Style features based on district data
    const getFeatureStyle = (feature: google.maps.Data.Feature, isHovered: boolean = false) => {
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtData = getDistrictData(districtNameEn)
      const concentrationLevel = districtData?.concentration_level || 'low'

      return {
        fillColor: getConcentrationColor(concentrationLevel, isHovered),
        fillOpacity: isHovered ? 0.85 : 0.65,
        strokeColor: isHovered ? '#1e3a5f' : '#64748b', // Navy when hover
        strokeWeight: isHovered ? 3 : 1,
        strokeOpacity: isHovered ? 0.9 : 0.5,
      }
    }

    map.data.setStyle(feature => getFeatureStyle(feature, false))

    // Create InfoWindow
    const infoWindow = new google.maps.InfoWindow()

    // Hover effect
    const mouseoverListener = map.data.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
      const feature = event.feature
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtNameTh = String(feature.getProperty('district_name_th'))
      const districtData = getDistrictData(districtNameEn)

      currentHoveredFeature = feature
      map.data.overrideStyle(feature, getFeatureStyle(feature, true))

      if (districtData) {
        const content = `
          <div style="font-family: 'IBM Plex Sans Thai', sans-serif; padding: 8px; min-width: 220px;">
            <h3 style="font-weight: 600; font-size: 15px; margin-bottom: 8px; color: #1e3a5f;">${districtNameTh}</h3>
            <div style="font-size: 13px; line-height: 1.6;">
              <div style="margin-bottom: 4px;">
                <strong>ระดับความเข้มข้น:</strong>
                <span style="color: ${districtData.concentration_level === 'high' ? '#ef4444' : districtData.concentration_level === 'medium' ? '#f97316' : '#22c55e'}; font-weight: 600;">
                  ${getConcentrationLabel(districtData.concentration_level)}
                </span>
              </div>
              <div style="margin-bottom: 4px;"><strong>ผู้ป่วยทั้งหมด:</strong> ${String(districtData.count.toLocaleString('th-TH'))} คน</div>
              ${districtData.by_type ? `
                <div style="margin-bottom: 4px;"><strong>ติดบ้าน:</strong> ${String(districtData.by_type.home.toLocaleString('th-TH'))} คน</div>
                <div style="margin-bottom: 4px;"><strong>ติดเตียง:</strong> ${String(districtData.by_type.bed.toLocaleString('th-TH'))} คน</div>
              ` : ''}
              ${districtData.by_severity ? `
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb;">
                  <div style="font-size: 12px; color: #ef4444;"><strong>เสี่ยงสูง:</strong> ${String(districtData.by_severity.high.toLocaleString('th-TH'))} คน</div>
                  <div style="font-size: 12px; color: #f97316;"><strong>ปานกลาง:</strong> ${String(districtData.by_severity.medium.toLocaleString('th-TH'))} คน</div>
                  <div style="font-size: 12px; color: #22c55e;"><strong>ต่ำ:</strong> ${String(districtData.by_severity.low.toLocaleString('th-TH'))} คน</div>
                </div>
              ` : ''}
            </div>
          </div>
        `
        infoWindow.setContent(content)
        infoWindow.setPosition(event.latLng)
        infoWindow.open(map)
      }
    })

    const mouseoutListener = map.data.addListener('mouseout', () => {
      if (currentHoveredFeature) {
        map.data.revertStyle(currentHoveredFeature)
        currentHoveredFeature = null
      }
      infoWindow.close()
    })

    // Cleanup
    return () => {
      google.maps.event.removeListener(mouseoverListener)
      google.maps.event.removeListener(mouseoutListener)
      infoWindow.close()
    }

  }, [map, geoJsonData, districts])

  return null
}

function BedriddenMapPolygonInner({
  districts = [],
  className = '',
  selectedDistrictId = null,
  selectedDistrictName = null
}: BedriddenMapPolygonProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  const [geoJsonData, setGeoJsonData] = useState<any>(null)

  // Load GeoJSON
  useEffect(() => {
    fetch('/geojson/nakhon_ratchasima_districts.json')
      .then(res => res.json())
      .then(data => {
        logger.debug('✅ District GeoJSON loaded:', data.features.length, 'districts')
        setGeoJsonData(data)
      })
      .catch(err => logger.error('❌ Failed to load GeoJSON:', err))
  }, [])

  // Filter districts by name
  const displayDistricts = selectedDistrictName
    ? districts.filter(d => d.district_name_th === selectedDistrictName)
    : districts

  if (!apiKey) {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">ยังไม่ได้ตั้งค่า Google Maps API Key</h3>
            <p className="text-sm text-amber-700 mt-1">
              กรุณาเพิ่ม <code className="bg-amber-100 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> ในไฟล์ <code className="bg-amber-100 px-1 rounded">.env</code>
            </p>
          </div>
        </div>
      </div>
    )
  }

  const center = { lat: 15.0, lng: 102.1 }
  const zoom = selectedDistrictId ? 10 : 8.5

  return (
    <div className={className}>
      {/* Info */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>📍 แผนที่ผู้ป่วยติดบ้าน/ติดเตียง:</strong> แสดงความหนาแน่นในแต่ละอำเภอด้วยสี
          {selectedDistrictName && ` | 🎯 กรองแสดง: ${selectedDistrictName}`}
          <br />
          <span className="text-xs">
            🔴 เข้มข้น ≥50 | 🟠 ปานกลาง 20-49 | 🟢 น้อย &lt;20 | 💡 Hover เพื่อดูรายละเอียด
          </span>
        </p>
      </div>

      <APIProvider apiKey={apiKey}>
        <div className="h-[500px] rounded-xl overflow-hidden border-2 border-slate-200 shadow-lg">
          <GoogleMap
            defaultCenter={center}
            defaultZoom={zoom}
            disableDefaultUI={false}
            gestureHandling="greedy"
          >
            {geoJsonData && displayDistricts.length > 0 && (
              <MapDataLayer
                districts={displayDistricts}
                geoJsonData={geoJsonData}
              />
            )}
          </GoogleMap>
        </div>
      </APIProvider>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.65)' }}></div>
          <span className="text-slate-600">น้อย (&lt;20)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(249, 115, 22, 0.65)' }}></div>
          <span className="text-slate-600">ปานกลาง (20-49)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.65)' }}></div>
          <span className="text-slate-600">เข้มข้น (≥50)</span>
        </div>
      </div>
    </div>
  )
}

export function BedriddenMapPolygon(props: BedriddenMapPolygonProps) {
  return (
    <MapErrorBoundary>
      <BedriddenMapPolygonInner {...props} />
    </MapErrorBoundary>
  )
}
